import React, { useMemo, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";

// Grille régulière : c'est ça qui crée les lignes parallèles visibles
const RINGS = 48; // lignes de latitude
const SEGS  = 72; // points par anneau
const COUNT = RINGS * SEGS;

const buildGridData = () => {
    const basePositions = new Float32Array(COUNT * 3);
    const workPositions = new Float32Array(COUNT * 3);
    const phases        = new Float32Array(COUNT);

    let idx = 0;
    for (let ri = 0; ri < RINGS; ri++) {
        // Distribution uniforme de -PI/2 à +PI/2
        const phi  = -Math.PI / 2 + (Math.PI / (RINGS - 1)) * ri;
        const cosP = Math.cos(phi);
        const sinP = Math.sin(phi);

        for (let si = 0; si < SEGS; si++) {
            const theta = (2 * Math.PI / SEGS) * si;

            const x = cosP * Math.cos(theta);
            const y = sinP;
            const z = cosP * Math.sin(theta);

            basePositions[idx * 3]     = x;
            basePositions[idx * 3 + 1] = y;
            basePositions[idx * 3 + 2] = z;
            workPositions[idx * 3]     = x;
            workPositions[idx * 3 + 1] = y;
            workPositions[idx * 3 + 2] = z;

            // Phase unique par particule basée sur sa position sur la grille
            phases[idx] = ri * 0.31 + si * 0.17;
            idx++;
        }
    }

    return { basePositions, workPositions, phases };
};

const ParticleCloud = React.memo(() => {
    const groupRef  = useRef(null);
    const pointsRef = useRef(null);

    const { basePositions, workPositions, phases } = useMemo(() => buildGridData(), []);

    useFrame(({ clock }) => {
        if (!groupRef.current || !pointsRef.current) return;
        const t = clock.getElapsedTime();
        const attr = pointsRef.current.geometry.attributes.position;
        const arr  = attr.array;

        for (let i = 0; i < COUNT; i++) {
            const bx = basePositions[i * 3];
            const by = basePositions[i * 3 + 1];
            const bz = basePositions[i * 3 + 2];

            // 3 fréquences superposées → respiration organique sur grille régulière
            const breathe = 1
                + 0.10 * Math.sin(t * 0.55 + phases[i])
                + 0.05 * Math.sin(t * 1.20 + phases[i] * 2.1)
                + 0.02 * Math.sin(t * 2.50 + phases[i] * 3.7);

            arr[i * 3]     = bx * breathe;
            arr[i * 3 + 1] = by * breathe;
            arr[i * 3 + 2] = bz * breathe;
        }

        attr.needsUpdate = true;

        groupRef.current.rotation.y += 0.0012;
        groupRef.current.rotation.x = Math.sin(t * 0.15) * 0.04;
    });

    return (
        <group ref={groupRef}>
            <points ref={pointsRef} frustumCulled={false} scale={[3.15, 3.15, 3.15]}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={workPositions.length / 3}
                        array={workPositions}
                        itemSize={3}
                        usage={35048}
                    />
                </bufferGeometry>
                <pointsMaterial
                    color="#2b3648"
                    size={0.038}
                    sizeAttenuation
                    transparent
                    opacity={0.1}
                    depthWrite={false}
                />
            </points>
        </group>
    );
});

export const AnimatedParticleSphereBackground = () => {
    return (
        <Canvas
            camera={{ position: [0, 0, 5.5], fov: 42 }}
            dpr={[1, 1.35]}
            gl={{ antialias: true, alpha: true }}
            style={{ pointerEvents: "none" }}
        >
            <ParticleCloud />
        </Canvas>
    );
};